import React, { Fragment, useState, useEffect, useContext } from 'react';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import EditIcon from '@material-ui/icons/Edit';
import UndoIcon from '@material-ui/icons/Undo';
import DoneIcon from '@material-ui/icons/Done';
import PublishIcon from '@material-ui/icons/Publish';
import Tooltip from '@material-ui/core/Tooltip';
import ShareIcon from '@material-ui/icons/Share';
import Snackbar from '@material-ui/core/Snackbar';
import QuestionContext from '../../QuestionContext'
import SearchDialog from '../SearchDialog';
import _ from 'lodash';
import ReactDragListView from 'react-drag-listview';

const EditQuestion = React.memo((props) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [fieldData, setFieldData] = useState(_.cloneDeep(props.field));
    const [openQuestionSearch, setOpenQuestionSearch] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [isNewQuestion, setIsNewQuestion] = useState(false);
    const [hasLogic, setHasLogic] = useState(null);

    const existingQuestions = useContext(QuestionContext);
    const { idx, field, deleteQuestion, deleteGroupQuestion, label, language, isGroupItem,
        fields, handleFieldPositionChange, disabled, toggleDisabled, logic,
        groupFieldChange, handleSaveEdits, addGroupChoice, deleteGroupChoice, handleFormChange,
        toggleSaveAlert, editPage, handleSaveGroupQuestion } = props;

    useEffect(() => {
        if (!isGroupItem && (props.field !== fieldData)) {
            setFieldData(_.cloneDeep(props.field));
        }
        let doesHaveLogic = logic ? logic.find(jump => jump.ref === props.field.ref) : null;
        setHasLogic(doesHaveLogic);
    }, [props.field]) // eslint-disable-line

    useEffect(() => {
        if (isGroupItem && props.groupData.properties.fields && (!_.isEqual(props.groupData.properties.fields[idx], fieldData))) {
            setFieldData(_.cloneDeep(props.groupData.properties.fields[idx]));
        }
    }, [props.groupData]) // eslint-disable-line

    useEffect(() => {
        let existingQuestion = existingQuestions.find((q) => q.ref === props.field.ref && q.language === language);
        if (!existingQuestion) {
            setIsNewQuestion(true);
        } else {
            setIsNewQuestion(false);
        }
    }, [props.field.ref, existingQuestions.length]) // eslint-disable-line

    function fieldChange(value, path, groupIdx = null) {
        let dataCopy = _.cloneDeep(fieldData);
        _.set(dataCopy, path, value);
        if (path.includes('type') && value === 'opinion_scale') {
            let newProperties = {
                start_at_one: true,
                steps: 5,
                labels: { left: '', center: '', right: '' }
            };
            if (groupIdx || groupIdx === 0) {
                dataCopy["properties"]["fields"][groupIdx]["properties"] = newProperties;
            } else {
                dataCopy["properties"] = newProperties;
            }
        } else if (path.includes('type') && value === 'multiple_choice') {
            let newProperties = {
                choices: [],
                allow_multiple_selection: false,
                allow_other_choice: false
            };
            if (groupIdx || groupIdx === 0) {
                dataCopy["properties"]["fields"][groupIdx]["properties"] = newProperties;
            } else {
                dataCopy["properties"] = newProperties;
            }
        } else if (path === 'type' && value === 'group') {
            let newProperties = {
                fields: [],
                button_text: "Continue",
                show_button: true
            };
            if (groupIdx || groupIdx === 0) {
                dataCopy["properties"]["fields"][groupIdx]["properties"] = newProperties;
            } else {
                dataCopy["properties"] = newProperties;
                if (dataCopy.validations) delete dataCopy.validations;
            }
        } else if (path.includes('type') && value === 'statement') {
            if (groupIdx || groupIdx === 0) {
                dataCopy["properties"]["fields"][groupIdx]["properties"] = {};
                if (dataCopy["properties"]["fields"][groupIdx].validations) delete dataCopy["properties"]["fields"][groupIdx].validations;
            } else {
                dataCopy["properties"] = {};
                if (dataCopy.validations) delete dataCopy.validations;
            }
        } else if (path.includes('type')) {
            if (groupIdx || groupIdx === 0) {
                dataCopy["properties"]["fields"][groupIdx]["properties"] = {};
            } else {
                dataCopy["properties"] = {};
            }
        }
        setFieldData(dataCopy);
    }

    function fieldGroupAddQuestion() {
        let dataCopy = { ...fieldData };
        dataCopy["properties"]["fields"].push({
            title: '',
            ref: '',
            type: 'yes_no',
            validations: { required: true },
            properties: {}
        });
        setFieldData(dataCopy);
    }

    function fieldDeleteGroupQuestion(deleteIdx) {
        let dataCopy = { ...fieldData };
        dataCopy["properties"]["fields"].splice(deleteIdx, 1);
        setFieldData(dataCopy);
    }

    function fieldGroupAddChoice(groupIdx) {
        let dataCopy = { ...fieldData };
        dataCopy["properties"]["fields"][groupIdx]["properties"]["choices"].push({
            label: ''
        });
        setFieldData(dataCopy);
    }

    function fieldAddChoice() {
        let dataCopy = { ...fieldData };
        if (dataCopy["properties"]["choices"]) {
            dataCopy["properties"]["choices"].push({
                label: ''
            });
        } else {
            dataCopy["properties"]["choices"] = [{ label: '' }];
        }
        setFieldData(dataCopy);
    }

    function fieldDeleteChoice(choiceIdx) {
        let dataCopy = { ...fieldData };
        dataCopy["properties"]["choices"].splice(choiceIdx, 1);
        setFieldData(dataCopy);
    }

    function fieldGroupDeleteChoice(groupIdx, choiceIdx) {
        let dataCopy = { ...fieldData };
        dataCopy["properties"]["fields"][groupIdx]["properties"]["choices"].splice(choiceIdx, 1);
        setFieldData(dataCopy);
    }

    function toggleQuestionSearch() {
        setOpenQuestionSearch(!openQuestionSearch);
    }

    function onRowClick(data) {
        let dataCopy = { ...fieldData };
        let clonedData = _.cloneDeep(data);
        let newQuestion = {
            title: clonedData.title,
            ref: clonedData.ref,
            type: clonedData.type,
            properties: clonedData.properties
        }
        if (data.type !== 'group') {
            if (data.type !== 'statement') {
                newQuestion.validations = { required: true };
            } else {
                newQuestion.properties.button_text = "Continue";
            }
            if (dataCopy["properties"]["fields"]) {
                dataCopy["properties"]["fields"].push(newQuestion);
            } else {
                dataCopy["properties"]["fields"] = [newQuestion];
            }
            setFieldData(dataCopy);
            toggleQuestionSearch();
        } else {
            setSnackbarMessage("Can't add group question to group");
            setOpenSnackbar(true);
        }
    }

    function handleChange(value, path) {
        if (isGroupItem) {
            let groupPath = `properties.fields[${idx}].${path}`;
            groupFieldChange(value, groupPath, idx);
        } else {
            fieldChange(value, path);
        }
    }

    function handleDelete() {
        if (isGroupItem) {
            deleteGroupQuestion(idx);
        } else {
            deleteQuestion(idx);
        }
    }

    function addChoice() {
        if (isGroupItem) {
            addGroupChoice(idx);
        } else {
            fieldAddChoice();
        }
    }

    function handleKeyDown(event, label, choiceIdx) {
        if (event.keyCode === 8 && label === "") {
            if (isGroupItem) {
                deleteGroupChoice(idx, choiceIdx);
            } else {
                fieldDeleteChoice(choiceIdx);
            }
        }
    }

    function handleMenuItemClick(event, newPosition) {
        if (!disabled && (newPosition !== idx)) {
            setSnackbarMessage("Please save edits before switching positions");
            setOpenSnackbar(true);
        } else if (newPosition !== idx) {
            handleFieldPositionChange(idx, newPosition);
        }
        setAnchorEl(null);
    }

    function handleClick(event) {
        setAnchorEl(event.currentTarget);
    };

    function handleClose() {
        setAnchorEl(null);
    };

    function saveEdits() {
        handleSaveEdits(idx, fieldData);
    }

    function cancelEdits() {
        setFieldData(_.cloneDeep(props.field));
        toggleDisabled(idx);
    }

    function onDragEnd(fromIndex, toIndex) {
        let dataCopy = [...fieldData.properties.choices];
        const item = dataCopy.splice(fromIndex, 1)[0];
        dataCopy.splice(toIndex, 0, item);
        if (isGroupItem) {
            let groupPath = `properties.fields[${idx}].properties.choices`;
            groupFieldChange(dataCopy, groupPath, idx);
        } else {
            fieldChange(dataCopy, 'properties.choices');
        }
    }



    return (
        <div style={{ display: 'flex', padding: 5, margin: 5, position: 'relative', width: '100%' }}>
            <div style={{ display: 'flex', flexDirection: 'column', width: '48%', marginRight: 10 }}>
                <Fragment>
                    <h4 aria-controls="simple-menu" aria-haspopup="true" onClick={!isGroupItem ? handleClick : undefined} style={{ marginTop: 0, cursor: isGroupItem ? '' : 'pointer' }}>
                        {label}
                    </h4>
                    {!isGroupItem &&
                        <Menu
                            anchorEl={anchorEl}
                            keepMounted
                            open={Boolean(anchorEl)}
                            onClose={handleClose}
                            style={{ zIndex: 10 }}
                        >
                            {fields.map((field, menuIdx) => (
                                <MenuItem
                                    key={menuIdx}
                                    selected={menuIdx === idx}
                                    onClick={(event) => handleMenuItemClick(event, menuIdx)}
                                >
                                    {`${menuIdx + 1}. ${field.ref}`}
                                </MenuItem>
                            ))}
                        </Menu>
                    }
                </Fragment>
                <label style={styles["label"]}> Question Text </label>
                <TextField
                    multiline
                    rowsMax={10}
                    value={disabled ? field.title : fieldData.title}
                    onChange={(e) => handleChange(e.target.value, 'title')}
                    autoComplete="off"
                    disabled={disabled}
                />
                {
                    ((disabled && field.type === 'opinion_scale') || (!disabled && fieldData.type === 'opinion_scale')) &&
                    <Fragment>
                        <label style={styles["label"]}>Start at 1?</label>
                        <select value={disabled ? field.properties.start_at_one : fieldData.properties.start_at_one}
                            onChange={(e) => handleChange(trueOrFalse(e.target.value), 'properties.start_at_one')}
                            style={styles["select"]} disabled={disabled}>
                            <option value={false}> False </option>
                            <option value={true}> True </option>
                        </select>
                        <label style={styles["label"]}>Number of steps</label>
                        <select value={disabled ? field.properties.steps : fieldData.properties.steps}
                            onChange={(e) => handleChange(parseInt(e.target.value), 'properties.steps')}
                            style={styles["select"]} disabled={disabled}>
                            <option value={11}> 11 </option>
                            <option value={10}> 10 </option>
                            <option value={9}> 9 </option>
                            <option value={8}> 8 </option>
                            <option value={7}> 7 </option>
                            <option value={6}> 6 </option>
                            <option value={5}> 5 </option>
                        </select>
                        <div style={{ width: '100%', display: 'flex' }}>
                            <div style={{ width: '30%', margin: 3 }}>
                                <label style={styles["label"]}>{isGroupItem ? "Left" : "Left Label"}</label>
                                <input
                                    style={{ ...styles["input"], width: '100%', opacity: disabled ? 0.5 : 1 }}
                                    value={disabled ? (field.properties.labels ? field.properties.labels.left : '') : (fieldData.properties.labels ? fieldData.properties.labels.left : '')}
                                    onChange={(e) => handleChange(e.target.value, 'properties.labels.left')}
                                    autoComplete="off"
                                    disabled={disabled}
                                />
                            </div>
                            <div style={{ width: '30%', margin: 3 }}>
                                <label style={styles["label"]}>{isGroupItem ? "Center" : "Center Label"}</label>
                                <input
                                    style={{ ...styles["input"], width: '100%', opacity: disabled ? 0.5 : 1 }}
                                    value={disabled ? (field.properties.labels ? field.properties.labels.center : '') : (fieldData.properties.labels ? fieldData.properties.labels.center : '')}
                                    onChange={(e) => handleChange(e.target.value, 'properties.labels.center')}
                                    autoComplete="off"
                                    disabled={disabled}
                                />
                            </div>
                            <div style={{ width: '30%', margin: 3 }}>
                                <label style={styles["label"]}>{isGroupItem ? "Right" : "Right Label"}</label>
                                <input
                                    style={{ ...styles["input"], width: '100%', opacity: disabled ? 0.5 : 1 }}
                                    value={disabled ? (field.properties.labels ? field.properties.labels.right : '') : (fieldData.properties.labels ? fieldData.properties.labels.right : '')}
                                    onChange={(e) => handleChange(e.target.value, 'properties.labels.right')}
                                    autoComplete="off"
                                    disabled={disabled}
                                />
                            </div>
                        </div>
                    </Fragment>
                }
                {(disabled && field.type === 'group') &&
                    <Fragment>
                        {field.properties.fields &&
                            <Fragment>
                                {field.properties.fields.map((groupField, groupIdx) => {
                                    let groupLabel = `${String.fromCharCode(97 + groupIdx)}.`
                                    if (idx || idx === 0) {
                                        groupLabel = (idx + 1) + groupLabel;
                                    }
                                    return (
                                        <EditQuestion
                                            key={groupIdx}
                                            fieldIdx={idx}
                                            field={groupField}
                                            idx={groupIdx}
                                            label={groupLabel}
                                            isGroupItem={true}
                                            disabled={disabled}
                                            fields={fieldData}
                                            groupData={fieldData}
                                            handleSaveGroupQuestion={handleSaveGroupQuestion}
                                            language={language}
                                        />
                                    )
                                })}
                            </Fragment>
                        }
                    </Fragment>
                }
                {(!disabled && fieldData.type === 'group') &&
                    <Fragment>
                        {fieldData.properties.fields &&
                            <Fragment>
                                {fieldData.properties.fields.map((groupField, groupIdx) => {
                                    let groupLabel = `${String.fromCharCode(97 + groupIdx)}.`
                                    if (idx || idx === 0) {
                                        groupLabel = (idx + 1) + groupLabel;
                                    }
                                    return (
                                        <EditQuestion
                                            key={groupIdx}
                                            fieldIdx={idx}
                                            field={groupField}
                                            idx={groupIdx}
                                            label={groupLabel}
                                            isGroupItem={true}
                                            disabled={disabled}
                                            fields={fieldData}
                                            groupData={fieldData}
                                            deleteGroupQuestion={fieldDeleteGroupQuestion}
                                            addGroupChoice={fieldGroupAddChoice}
                                            groupFieldChange={fieldChange}
                                            deleteGroupChoice={fieldGroupDeleteChoice}
                                            handleSaveGroupQuestion={handleSaveGroupQuestion}
                                            language={language}
                                        />
                                    )
                                })}
                            </Fragment>
                        }
                        <div style={{ display: 'flex' }}>
                            <button style={{ ...styles['button'], opacity: disabled ? 0.5 : 1 }} onClick={fieldGroupAddQuestion} disabled={disabled}>Add new question to group</button>
                            <button style={{ ...styles['button'], opacity: disabled ? 0.5 : 1 }} onClick={toggleQuestionSearch} disabled={disabled}>Add existing question to group</button>
                        </div>
                    </Fragment>
                }
                {
                    ((disabled && field.type === 'multiple_choice') || (!disabled && fieldData.type === 'multiple_choice')) &&
                    <div style={{ display: 'flex', flexDirection: 'column' }}>

                        <label style={styles["label"]}>Allow Multiple Selection</label>
                        <input
                            type="checkbox"
                            name="Multiple Selection"
                            checked={disabled ? field.properties.allow_multiple_selection : fieldData.properties.allow_multiple_selection}
                            onChange={(e) => handleChange(!fieldData.properties.allow_multiple_selection, "properties.allow_multiple_selection")}
                            style={{ height: 20, width: 20, marginLeft: 15 }}
                            disabled={disabled}
                        />
                        <label style={styles["label"]}>Allow Other Choice</label>
                        <input
                            type="checkbox"
                            name="Other Choice"
                            checked={disabled ? field.properties.allow_other_choice : fieldData.properties.allow_other_choice}
                            onChange={(e) => handleChange(!fieldData.properties.allow_other_choice, "properties.allow_other_choice")}
                            style={{ height: 20, width: 20, marginLeft: 15 }}
                            disabled={disabled}
                        />
                        {disabled ?
                            <Fragment>
                                {field.properties.choices &&
                                    <Fragment>
                                        {
                                            field.properties.choices.map((choice, choiceIdx) => {
                                                return (
                                                    <Fragment key={choiceIdx}>
                                                        <label style={styles["label"]}>{"Choice " + (choiceIdx + 1)} </label>
                                                        <input
                                                            style={{ ...styles["input"], opacity: disabled ? 0.5 : 1 }}
                                                            value={choice.label}
                                                            disabled={disabled}
                                                        />
                                                    </Fragment>
                                                )
                                            })
                                        }
                                    </Fragment>
                                }
                            </Fragment>
                            :
                            <ReactDragListView nodeSelector="div" handleSelector="label" onDragEnd={onDragEnd}>
                                {fieldData.properties.choices &&
                                    fieldData.properties.choices.map((choice, choiceIdx) => (
                                        <div key={choiceIdx}>
                                            <label style={styles["label"]}>{"Choice " + (choiceIdx + 1)} </label>
                                            <input
                                                style={{ ...styles["input"], opacity: disabled ? 0.5 : 1 }}
                                                value={choice.label}
                                                onChange={(e) => handleChange(e.target.value, `properties.choices[${choiceIdx}].label`)}
                                                onKeyDown={(e) => handleKeyDown(e, choice.label, choiceIdx)}
                                                autoComplete="off"
                                                disabled={disabled}
                                            />
                                        </div>
                                    ))
                                }
                            </ReactDragListView>
                        }
                        {!disabled &&
                            <button style={{ ...styles['button'], opacity: disabled ? 0.5 : 1 }}
                                onClick={addChoice}
                                disabled={disabled}>
                                Add Choice
                            </button>
                        }
                    </div>
                }
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', width: '48%' }}>
                <h4 style={{ marginTop: 0 }}> Details </h4>
                <Tooltip title="Delete Question" placement="top" arrow>
                    <span style={{ position: 'absolute', right: 20 }}>
                        <IconButton onClick={handleDelete} disabled={(disabled && isGroupItem)}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                {(isGroupItem && isNewQuestion) &&
                    <Tooltip title="Upload Question" placement="top" arrow>
                        <span style={{ position: 'absolute', right: 50 }}>
                            <IconButton onClick={() => handleSaveGroupQuestion(fieldData)}>
                                <PublishIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                }
                {!isGroupItem &&
                    <Fragment>
                        {disabled ?
                            <Fragment>
                                <Tooltip title="Edit" placement="top" arrow>
                                    <IconButton style={{ position: 'absolute', right: 50 }} onClick={() => toggleDisabled(idx)}>
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                {(field.type === 'multiple_choice' || field.type === 'yes_no') &&
                                    <Tooltip title="Logic Jump" placement="top" arrow>
                                        <IconButton style={{ position: 'absolute', right: 80 }} onClick={() => handleFormChange(idx, 'currentLogicQuestion')}>
                                            <ShareIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                }
                                {(isNewQuestion && editPage) &&
                                    <Tooltip title="Add to question store" placement="top" arrow>
                                        <IconButton style={{ position: 'absolute', right: (field.type === 'multiple_choice' || field.type === 'yes_no') ? 110 : 80 }} onClick={() => toggleSaveAlert(idx)}>
                                            <PublishIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                }
                            </Fragment>
                            :
                            <Fragment>
                                <Tooltip title="Save Edits" placement="top" arrow>
                                    <IconButton style={{ position: 'absolute', right: 50 }} onClick={saveEdits}>
                                        <DoneIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Undo Edits" placement="top" arrow>
                                    <IconButton style={{ position: 'absolute', right: 80 }} onClick={cancelEdits}>
                                        <UndoIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Fragment>
                        }
                    </Fragment>
                }
                <label style={styles["label"]}>Question Ref</label>
                <input
                    style={{ ...styles["input"], opacity: disabled ? 0.5 : 1 }}
                    value={disabled ? field.ref : fieldData.ref}
                    onChange={(e) => handleChange(e.target.value, 'ref')}
                    autoComplete="off"
                    disabled={disabled}
                />
                {field.properties &&
                    <Fragment>
                        <label style={styles["label"]}>Description</label>
                        <input
                            style={{ ...styles["input"], opacity: disabled ? 0.5 : 1 }}
                            value={disabled ? (field.properties.description ? field.properties.description : '') : (fieldData.properties.description ? fieldData.properties.description : '')}
                            onChange={(e) => handleChange(e.target.value, 'properties.description')}
                            autoComplete="off"
                            disabled={disabled}
                        />
                    </Fragment>
                }
                {requiredValidation.includes(field.type) &&
                    <Fragment>
                        <label style={styles["label"]}>Required</label>
                        <input
                            type="checkbox"
                            name="Required"
                            checked={disabled ? (field.validations ? field.validations.required : false) : (fieldData.validations ? fieldData.validations.required : false)}
                            onChange={() => handleChange(!fieldData.validations.required, 'validations.required')}
                            style={{ height: 20, width: 20, marginLeft: 15 }}
                            disabled={disabled}
                        />
                    </Fragment>
                }
                <select
                    value={disabled ? field.type : fieldData.type}
                    onChange={(e) => handleChange(e.target.value, 'type')}
                    style={styles["select"]} disabled={disabled}>
                    {questionTypes.map(type => {
                        if (!isGroupItem || type.value !== 'group') {
                            return (
                                <option key={type.value} value={type.value}> {type.label} </option>
                            )
                        } else {
                            return null;
                        }
                    })}
                </select>
                {hasLogic &&
                    hasLogic.actions.map((action, idx) => (
                        <p key={idx}> {`jumps to ${action.details.to.value}`} </p>
                    ))
                }
            </div>
            <Snackbar
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                open={openSnackbar}
                autoHideDuration={5000}
                onClose={() => setOpenSnackbar(false)}
                message={snackbarMessage}
                action={
                    <IconButton size="small" aria-label="close" color="inherit" onClick={() => setOpenSnackbar(false)}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                }
                style={{ fontSize: 14 }}
            />
            {existingQuestions &&
                <SearchDialog
                    data={existingQuestions}
                    open={openQuestionSearch}
                    toggleOpen={toggleQuestionSearch}
                    onRowClick={onRowClick}
                    languageFilter={language}
                />
            }
        </div>
    )
});

function trueOrFalse(value) {
    if (value === 'true' || value === true) return true;
    else if (value === 'false' || value === false) return false;
    else return;
}

const styles = {
    'input': {
        backgroundColor: 'white',
        border: 'none',
        borderBottom: '2px solid #B8D0EF',
        outline: 'none',
        width: '100%',
        fontSize: 1 + "rem",
        fontWeight: 'regular',
        lineHeight: 1.6,
        marginBottom: 15,
        boxShadow: 'none',
        boxSizing: 'border-box',
        borderRadius: "4px 4px 0 0",
    },
    'select': {
        fontSize: 14,
        width: '60%',
        margin: 5,
        height: 40
    },
    'label': {
        fontSize: 14,
        marginBottom: 0,
        textTransform: 'capitalize',
        color: '#0256c5',
        paddingLeft: 1 + "rem"
    },
    'button': {
        backgroundColor: '#f0a34e',
        color: 'white',
        cursor: 'pointer',
        borderRadius: 8,
        fontSize: 12,
        padding: 8,
        margin: 10,
        width: '50%',
    }
}

const requiredValidation = [
    'email',
    'long_text',
    'short_text',
    'multiple_choice',
    'opinion_scale',
    'phone_number',
    'yes_no'
]

const questionTypes = [
    { label: 'Yes/No', value: 'yes_no' },
    { label: 'Opinion scale', value: 'opinion_scale' },
    { label: 'Multiple choice', value: 'multiple_choice' },
    { label: 'Short text', value: 'short_text' },
    { label: 'Long text', value: 'long_text' },
    { label: 'Email', value: 'email' },
    { label: 'Group', value: 'group' },
    { label: 'Statement', value: 'statement' }
]

export default EditQuestion;